import React, { useEffect, useMemo, useState } from "react";

import * as yup from "yup";
import Close from "@mui/icons-material/Close";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  // Chip,
  Divider,
  Drawer as DrawerMui,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useFormik } from "formik";
import { useActionHook, useDidUpdate, useModals, useNavigationHooks, useUrlHooks } from "hooks";
import { isEqual, isNull } from "lodash";
import { IApiServiceError, useRolesSelector, useUserManageActions } from "store";
import { IRole } from "store/types";
import {
  IPermissionItem,
  IUserManage,
  IUserManageData,
  IUserManageInvestmentEntity,
  PermissionValueType,
  UserPermissionType
} from "store/types/user-management";
import apiErrorsToFormik from "utils/api-errors-to-formik";
import { toastify } from "utils/tyostify";

import { RoleName } from "../../../../constants";
import { InvestmentEntity } from "../Investment-entity-popup/InvestmentEntity";
import { PermissionIcon } from "./PermissionIcon";

// import { TeamPopup } from "../Team-popup/TeamPopup";
// import { GroupPopup } from "../Group-popup/GroupPopup";

const permissionRoles = [
  {
    name: "Editor",
    key: UserPermissionType.EDITOR
  },
  {
    name: "Viewer",
    key: UserPermissionType.VIEWER
  },
  {
    name: "Admin",
    key: UserPermissionType.ADMIN
  }
];

const allowAdminList = ["cloud_integrations", "characteristics"];

const validationSchema = yup.object({
  first_name: yup.string().required("First name is required field"),
  last_name: yup.string().required("Last name is required field"),
  email: yup.string().email("Invalid email format").required("Email is required field"),
  role: yup.object().nullable().required("Platform role is required field")
});

interface DrawerProps {
  permissionList: IPermissionItem[];
  refetch: () => void;
}

export const Drawer: React.FC<DrawerProps> = ({ permissionList, refetch }) => {
  const { queryParams } = useUrlHooks();
  const { userManagement, isContact } = queryParams;
  const { createInviteUserManageAction } = useUserManageActions();
  const { removeParams } = useNavigationHooks();
  const roles = useRolesSelector();
  const { confirmModal } = useModals();
  const [showFullName, setShowFullName] = useState<boolean>(false);

  const { action: createInvite } = useActionHook(createInviteUserManageAction, {
    onSuccess: () => toastify({ message: "The invitation has been successfully sent", place: "bottom-right" })
  });

  const transformInitialPermissions = (permissionsList: IPermissionItem[]) => {
    return permissionsList.reduce(
      (acc, { key }) => {
        acc[key] = UserPermissionType.NO_ACCESS; // Default all permissions to UserPermissionType.NO_ACCESS
        return acc;
      },
      {} as Record<string, UserPermissionType.NO_ACCESS>
    );
  };

  const initialValue = useMemo(() => {
    return {
      id: 0,
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "",
      permissions: transformInitialPermissions(permissionList),
      full_name: "",
      invite_user: false,
      investors: [],
      role: null!,
      note: null
    };
  }, [permissionList]);

  const { getUserAction, createUserManageAction, updateUserManageAction, getContactAction, updateContactAction } =
    useUserManageActions();
  // getUserAction or getContactAction is used depending on the isContact flag
  const {
    data: userData,
    action,
    loading: userDataLoading
  } = useActionHook(isContact ? getContactAction : getUserAction);

  const { action: createUser } = useActionHook(createUserManageAction, {
    onSuccess: () => {
      refetch();
      onInvalidateStates();
    },
    onReject: (error: IApiServiceError) => {
      const { errors } = error;
      setErrors(apiErrorsToFormik(initialValue, errors));
    }
  });

  const { action: updateUser } = useActionHook(isContact ? updateContactAction : updateUserManageAction, {
    onSuccess: () => {
      refetch();
      onInvalidateStates();
    },
    onReject: (error: IApiServiceError) => {
      const { errors } = error;
      setErrors(apiErrorsToFormik(initialValue, errors));
    }
  });

  // const { data: groups = [], action: getGroups } = useActionHook(getGroupsAction);
  // const { action: createGroup } = useActionHook(createGroupAction);
  // const { action: deleteGroup } = useActionHook(deleteGroupAction);

  // const onCreateGroup = useCallback(async (name: string) => {
  //   await createGroup(name);
  //   await getGroups();
  // }, []);

  useEffect(() => {
    if (!isNaN(Number(userManagement))) {
      action(Number(userManagement));
    }
  }, [userManagement, isContact]);

  // useEffect(() => {
  //   getGroups();
  // }, []);

  const { submitForm, isValid, values, touched, errors, resetForm, setValues, setErrors } = useFormik<IUserManage>({
    initialValues: initialValue,
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const transformedData: IUserManageData = {
        first_name: values.first_name,
        last_name: values.last_name,
        note: values.note,
        phone_number: values.phone_number,
        email: values.email,
        role_id: values.role.id,
        invite_user: values.invite_user,

        ...(values.id && { id: values.id })
      };

      if (isRoleInvestor) {
        transformedData.investor_ids = values.investors.map((entity) => entity.id);
      }

      if (isRoleTM) {
        //if role admin and guest ignore field permissions
        transformedData.permissions =
          values.permissions &&
          Object.entries(values.permissions).map(([permissionType, access]) => ({
            permission_type: permissionType,
            access
          }));
      }
      if (!isNaN(Number(userManagement))) {
        updateUser(transformedData);
      }

      if (isNaN(Number(userManagement))) {
        createUser(transformedData);
      }
    }
  });

  const onInvalidateStates = () => {
    resetForm();
    setShowFullName(false);
    removeParams(["userManagement", "isContact"]);
  };

  const onClose = () => {
    if (
      isRoleInvestor &&
      values.investors.length &&
      values.investors &&
      !isEqual(values.investors, userData?.investors)
    ) {
      confirmModal({
        msg: "Selected investment entities will not be saved. Are you sure you want to continue?",
        title: "Remove investment entities",
        action: onInvalidateStates
      });
      return;
    }
    onInvalidateStates();
  };

  const isRoleAdmin = values.role && values.role.name === RoleName.Admin;

  const isRoleTM = values.role && values.role.name === RoleName.Analyst;

  const isRoleInvestor = values.role && values.role.name === RoleName.Investor;

  const shouldShowPermissions = !userDataLoading && (isRoleAdmin || isRoleTM);

  // Contact role is synthetic role for user, can be created only in characteristics drawer "Contact" tab (id -1)
  const isRoleContact = useMemo(() => {
    return values.role && values.role.id === -1;
  }, [values.role]);

  useDidUpdate(() => {
    if (userData) {
      setValues(userData);
      setShowFullName(Boolean(userData.full_name));
    }
  }, [userData]);

  useEffect(() => () => removeParams(["userManagement"]), []);

  const handleCheckboxChange = (key: string, checked: boolean) => {
    setValues((prev) => {
      const updated = {
        ...prev.permissions,
        [key]: checked ? UserPermissionType.EDITOR : UserPermissionType.NO_ACCESS
      };
      // If characteristics is disabled, deals must follow
      if (key === "characteristics") {
        if (!checked && updated.deals && updated.deals !== UserPermissionType.NO_ACCESS) {
          updated.deals = UserPermissionType.NO_ACCESS;
        }
      }
      return { ...prev, permissions: updated };
    });
  };

  const handleSelectChange = (
    key: string,
    value: UserPermissionType.EDITOR | UserPermissionType.VIEWER | UserPermissionType.ADMIN
  ) => {
    setValues((prev) => {
      const updated = {
        ...prev.permissions,
        [key]: value
      };
      // If characteristics is set to viewer, deals can only be viewer
      if (key === "characteristics" && value === UserPermissionType.VIEWER) {
        if (updated.deals && updated.deals !== UserPermissionType.NO_ACCESS) {
          updated.deals = UserPermissionType.VIEWER;
        }
      }
      return { ...prev, permissions: updated };
    });
  };

  const handleChangeField = (
    field: keyof IUserManage,
    value: string | boolean | IRole | Record<string, PermissionValueType> | IUserManageInvestmentEntity[]
  ) => {
    setValues((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const setRoleAndPermissions = (value: IRole) => {
    if (value && value.name === "admin") {
      //handle admin
      setValues((prev) => {
        const updatedPermissions: IUserManage["permissions"] = {};
        permissionList.map(
          (perm) => (updatedPermissions[perm.key] = UserPermissionType.NO_ACCESS) //set in UI as all enabled permissions with values editor in selects
        );

        return {
          ...prev,
          role: value,
          permissions: updatedPermissions
        };
      });
      return;
    }

    if (value && value.name === "team-member") {
      if (value && userData && value.name === userData?.role.name) {
        setValues((prev) => ({ ...prev, role: value, permissions: userData.permissions })); //if user was TM, then admin changed his permission to admin locally and changed it again to TM we need set all permissions like it was before
      }
      setValues((prev) => {
        const updatedPermissions: IUserManage["permissions"] = {};
        permissionList.map(
          (perm) => (updatedPermissions[perm.key] = UserPermissionType.EDITOR) //set in UI as all enabled permissions with values editor in selects
        );

        return {
          ...prev,
          role: value,
          permissions: updatedPermissions
        };
      });
      return;
    }
    handleChangeField("role", value); //if user not admin nor TM, just change his role
  };

  return (
    <DrawerMui
      open={!!userManagement}
      onClose={onClose}
      sx={{
        ".MuiPaper-root": {
          borderTopRightRadius: "25px",
          borderBottomRightRadius: "25px",
          width: "520px"
        }
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          height: "50px",
          minHeight: "50px",
          padding: "0 25px",
          fontSize: "17px",
          backgroundColor: "#edf1f9",
          color: "#0a3142"
        }}
      >
        <Typography sx={{ fontSize: "16px", color: "#071F35" }}>User properties</Typography>
        <IconButton onClick={onClose}>
          <Close sx={{ fill: "#0a3142" }} />
        </IconButton>
      </Stack>
      <Box
        sx={{
          display: "flex",
          position: "relative",
          overflow: "hidden",
          flexGrow: 1,
          backgroundColor: "white"
        }}
      >
        <Stack justifyContent="space-between" alignItems="stretch" direction="column" overflow="hidden" flexGrow={1}>
          <Stack flexGrow={1} overflow="auto">
            <Stack gap="20px" sx={{ padding: "20px" }}>
              {showFullName ? (
                <Stack flexDirection="row" gap="10px" alignItems="center">
                  <Typography sx={{ color: "#071F35" }} fontSize="20px" fontWeight="500">
                    {values.full_name}
                  </Typography>
                  <IconButton onClick={() => setShowFullName(false)}>
                    <CreateOutlinedIcon />
                  </IconButton>
                </Stack>
              ) : (
                <Stack flexDirection="row" gap="10px">
                  <TextField
                    fullWidth
                    required
                    label="First Name"
                    value={values.first_name}
                    error={touched.first_name && Boolean(errors.first_name)}
                    helperText={touched.first_name && errors.first_name}
                    placeholder="First Name"
                    onChange={(e) => {
                      handleChangeField("first_name", e.target.value);
                    }}
                  />
                  <TextField
                    fullWidth
                    required
                    label="Last name"
                    value={values.last_name}
                    error={touched.last_name && Boolean(errors.last_name)}
                    helperText={touched.last_name && errors.last_name}
                    placeholder="Last name"
                    onChange={(e) => {
                      handleChangeField("last_name", e.target.value);
                    }}
                  />
                </Stack>
              )}
              <TextField
                fullWidth
                required
                label="Email"
                value={values.email}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
                type="email"
                placeholder="Email"
                onChange={(e) => {
                  handleChangeField("email", e.target.value);
                }}
              />
              <TextField
                fullWidth
                label="Phone"
                value={values.phone_number}
                error={touched.phone_number && Boolean(errors.phone_number)}
                helperText={touched.phone_number && errors.phone_number}
                placeholder="Phone"
                onChange={(e) => {
                  handleChangeField("phone_number", e.target.value);
                }}
              />
              <Autocomplete
                getOptionLabel={(option) => option.presentation_name}
                size="small"
                disableClearable
                options={
                  isRoleContact
                    ? [...roles, { id: -1, name: "Contact", presentation_name: "Contact", short_name: "Contact" }]
                    : roles
                }
                value={values.role}
                onChange={(_, value) => {
                  setRoleAndPermissions(value as IRole);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    required
                    size="small"
                    placeholder="Select"
                    label="Platform role"
                    InputLabelProps={{ shrink: true }}
                    error={touched.role && Boolean(errors.role)}
                    helperText={touched.role && (errors.role as string)}
                  />
                )}
              />
              {/* hide this field for contact role  id -1 */}
              {!isRoleContact && (
                <TextField
                  fullWidth
                  label="Note"
                  value={values.note}
                  placeholder="Note"
                  onChange={(e) => {
                    handleChangeField("note", e.target.value);
                  }}
                />
              )}
              {/* hide this field for contact role  id -1 */}
              {!isRoleContact && (
                <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
                  <FormControlLabel
                    sx={{
                      gap: "10px",
                      margin: 0,
                      ".MuiFormControlLabel-label": {
                        fontSize: "15px"
                      }
                    }}
                    control={
                      <Checkbox
                        onChange={() => handleChangeField("invite_user", !values.invite_user)}
                        checked={isNull(values.invite_user) ? true : values.invite_user}
                        disabled={
                          isNull(userData?.invite_user) || (!isNaN(Number(userManagement)) && userData?.invite_user)
                        }
                        sx={{
                          width: "20px",
                          height: "20px"
                        }}
                      />
                    }
                    label="Invite User to the Platform"
                  />
                  {!isNaN(Number(userManagement)) && userData?.invite_user && (
                    <Typography
                      sx={{ color: "#26A6AE", fontSize: "15px", cursor: "pointer" }}
                      onClick={() => createInvite(values.id!)}
                    >
                      Invite Again
                    </Typography>
                  )}
                </Stack>
              )}
              {/* <Stack>
              <Typography>Team</Typography>
              <Divider sx={{ borderColor: "#DBE8F5" }} />
              <Stack flexDirection="row" justifyContent="space-between" padding="10px 0px">
                <Stack flexDirection="row" gap="5px">
                  {data.map((team) => {
                    return (
                      <Chip
                        key={team}
                        label={team}
                        sx={{
                          backgroundColor: "#F6F8FC",
                          fontSize: "12px",
                          padding: "4px 8px",
                          height: "24px"
                        }}
                      />
                    );
                  })}
                </Stack>
                <TeamPopup icon={<EditOutlinedIcon />} />
              </Stack>
            </Stack> */}
              {/* hide this field for contact role  id -1 */}
              {!isRoleContact && isRoleInvestor && (
                <Stack>
                  <Typography>Investment Entity</Typography>
                  <Divider sx={{ borderColor: "#DBE8F5" }} />
                  <Stack flexDirection="row" justifyContent="space-between" padding="10px 0px">
                    <Stack flexDirection="row" gap="5px">
                      {values.investors.map((entity) => {
                        return (
                          <Chip
                            key={entity.id}
                            label={entity.name}
                            sx={{
                              backgroundColor: "#F6F8FC",
                              fontSize: "12px",
                              padding: "4px 8px",
                              height: "24px"
                            }}
                          />
                        );
                      })}
                    </Stack>
                    <IconButton sx={{ backgroundColor: "#F6F8FC", height: "24px", width: "24px" }}>
                      <InvestmentEntity
                        icon={<EditOutlinedIcon />}
                        userEntities={values.investors}
                        onCancel={() => handleChangeField("investors", userData?.investors || [])}
                        setUserEntities={(entities) => handleChangeField("investors", entities)}
                      />
                    </IconButton>
                  </Stack>
                </Stack>
              )}
              {/* <Stack>
              <Typography>Group</Typography>
              <Divider sx={{ borderColor: "#DBE8F5" }} />
              <Stack flexDirection="row" justifyContent="space-between" padding="10px 0px">
                <Stack flexDirection="row" gap="5px">
                  {groups.map((group) => {
                    return (
                      <Chip
                        key={group.id}
                        label={group.name}
                        sx={{
                          backgroundColor: "#F6F8FC",
                          fontSize: "12px",
                          padding: "4px 8px",
                          height: "24px"
                        }}
                      />
                    );
                  })}
                </Stack>
                <GroupPopup
                  groups={groups}
                  icon={<EditOutlinedIcon />}
                  onCreate={onCreateGroup}
                  onDelete={deleteGroup}
                />
              </Stack>
            </Stack> */}
              {/* hide this field for contact role  id -1 */}
              {!isRoleContact && shouldShowPermissions && values.role && (
                <Stack>
                  <Typography>Permissions</Typography>
                  <Divider sx={{ borderColor: "#DBE8F5" }} />
                  <Stack flexDirection="column" padding="10px 0px" gap="8px">
                    {permissionList.map(({ key, title }) => {
                      if (!values.permissions) return;
                      return (
                        <Stack
                          key={key}
                          flexDirection="row"
                          justifyContent="space-between"
                          alignItems="center"
                          width="100%"
                          sx={{ position: "relative" }}
                        >
                          <FormControlLabel
                            sx={{
                              gap: "16px",
                              margin: 0
                            }}
                            control={
                              <Checkbox
                                onChange={(e) => handleCheckboxChange(key, e.target.checked)}
                                checked={isRoleAdmin || values.permissions[key] !== UserPermissionType.NO_ACCESS}
                                disabled={isRoleAdmin}
                                sx={{
                                  width: "20px",
                                  height: "20px"
                                }}
                              />
                            }
                            label={
                              <Stack flexDirection="row" justifyContent="center" alignItems="center" gap="8px">
                                <PermissionIcon
                                  sx={{
                                    fill:
                                      isRoleAdmin || values.permissions[key] === UserPermissionType.NO_ACCESS
                                        ? "#92A7BC"
                                        : "#0a3142",
                                    color:
                                      isRoleAdmin || values.permissions[key] === UserPermissionType.NO_ACCESS
                                        ? "#92A7BC"
                                        : "#0a3142"
                                  }}
                                  type={key}
                                />
                                <Typography
                                  sx={{
                                    color:
                                      isRoleAdmin || values.permissions[key] === UserPermissionType.NO_ACCESS
                                        ? "#92A7BC"
                                        : "#0a3142"
                                  }}
                                >
                                  {title}
                                </Typography>
                              </Stack>
                            }
                          />
                          {key === "deals" && values.permissions?.characteristics === UserPermissionType.VIEWER && (
                            <MaterialTooltip
                              variant="dark"
                              isShow
                              title="Deal creation requires characteristics editing. When characteristics is set to viewer, deals permission is restricted to viewer as well."
                              placement="top"
                            >
                              <InfoOutlinedIcon
                                sx={{
                                  fontSize: "16px",
                                  color: "#071f35",
                                  position: "absolute",
                                  right: 146,
                                  top: "50%",
                                  transform: "translateY(-50%)"
                                }}
                              />
                            </MaterialTooltip>
                          )}
                          <TextField
                            select
                            fullWidth
                            sx={{ maxWidth: "142px" }}
                            margin="none"
                            size="small"
                            value={
                              values.permissions[key] === UserPermissionType.NO_ACCESS
                                ? UserPermissionType.EDITOR
                                : values.permissions[key]
                            }
                            onChange={(e) =>
                              handleSelectChange(
                                key,
                                e.target.value as
                                  | UserPermissionType.EDITOR
                                  | UserPermissionType.VIEWER
                                  | UserPermissionType.ADMIN
                              )
                            }
                            disabled={
                              isRoleAdmin ||
                              values.permissions[key] === UserPermissionType.NO_ACCESS ||
                              (key === "deals" && values.permissions?.characteristics === UserPermissionType.VIEWER)
                            }
                          >
                            {permissionRoles
                              .filter((role) => {
                                if (role.key === UserPermissionType.ADMIN) {
                                  return allowAdminList.includes(key);
                                }
                                // If characteristics is viewer, deals can only be viewer
                                if (
                                  key === "deals" &&
                                  values.permissions?.characteristics === UserPermissionType.VIEWER &&
                                  role.key !== UserPermissionType.VIEWER
                                ) {
                                  return false;
                                }
                                return true;
                              })
                              .map((role) => (
                                <MenuItem key={role.key} value={role.key}>
                                  {role.name}
                                </MenuItem>
                              ))}
                          </TextField>
                        </Stack>
                      );
                    })}
                  </Stack>
                </Stack>
              )}
            </Stack>
          </Stack>
          <Box
            sx={{
              height: "87px",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              padding: "30px",
              boxShadow: "0 -4px 13px 0 rgba(10, 49, 66, 0.2)",
              gap: "15px"
            }}
          >
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={submitForm} disabled={!isValid} type="submit" variant="contained">
              Save
            </Button>
          </Box>
        </Stack>
      </Box>
    </DrawerMui>
  );
};
